/**
 * DEV-2762 (эпик DEV-2755). Раздел «Коммуникации»: общий список чатов с клиентами
 * (messenger-providers). Рукописный axios, как telephony.api.ts: ручка новая,
 * массивы в query должны уходить повторами (`channels=a&channels=b`), а не `channels[]`.
 */
import { apiClient } from '@/api'

export type ChatChannel = 'telegram' | 'whatsapp' | 'zalo'
/** DEV-2807. Кто собеседник: клиент, агент по недвижимости или «другое» (спам, партнёры…). */
export type ChatCategory = 'client' | 'agent' | 'other'
export const CHAT_CATEGORIES: readonly ChatCategory[] = ['client', 'agent', 'other']
export const isChatCategory = (v: unknown): v is ChatCategory =>
  (CHAT_CATEGORIES as readonly unknown[]).includes(v)
/** DEV-2771. Последним в чате был звонок: '' — нет, missed — пропущенный входящий, call — любой другой. */
export type ChatLastCall = '' | 'missed' | 'call'

/**
 * Строка общего списка. Пустой clientId — личный Telegram-чат клиента, чью карточку не видно
 * (R8'), либо агент или «другое» без карточки (DEV-2807, только руководству).
 */
export interface AllChat {
  clientId: string
  clientName: string
  chatKey: string
  accountManagerId: string
  responsibleManagerId: string
  lastMessageTime: string
  lastMessagePreview: string
  lastMessageFromClient: boolean
  lastMessageCall: ChatLastCall
  /** DEV-1676. Кто из компании написал последнее: 'bot' — AI-ассистент, 'auto', 'manager'; '' — клиент или мимо CRM. */
  lastMessageAuthorType: string
  unreadCount: number
  channel: ChatChannel
  title: string
  category: ChatCategory
}

export interface ChatsQuery {
  pageSize?: number
  pageToken?: string
  managerId?: string
  clientIds?: string[]
  channels?: ChatChannel[]
  unreadOnly?: boolean
  awaitingReply?: boolean
  /** DEV-2807. Пусто — только клиенты. */
  categories?: ChatCategory[]
}

export interface ChatsPage {
  items: AllChat[]
  nextPageToken: string
}

interface ListChatsResponse {
  data?: Array<Partial<AllChat>>
  pagination?: { nextPageToken?: string }
}

export type CallSource = 'telephony' | 'telegram' | 'waba'
export type CallOutcome = 'answered' | 'missed' | 'declined' | 'busy' | 'failed'
export type LeadTemp = 'hot' | 'warm' | 'cold'

/**
 * DEV-2770. Строка общего списка звонков (telephony-service). Пустые clientId/clientName —
 * звонок клиента, чью карточку менеджер не видит (R8'). managerId пуст, если никто не снял трубку.
 */
export interface CommunicationCall {
  id: string
  source: CallSource
  direction: 'in' | 'out'
  outcome: CallOutcome
  managerId: string
  clientId: string
  clientName: string
  peerNumber: string
  startedAt: string
  durationSec: number
  hasRecording: boolean
  hasAnalysis: boolean
  leadTemperature: LeadTemp | ''
  /** 0 — не оценён, иначе 1..5. */
  usefulnessScore: number
}

export interface CallsQuery {
  pageSize?: number
  pageToken?: string
  managerId?: string
  clientIds?: string[]
  sources?: CallSource[]
  direction?: 'in' | 'out'
  answered?: boolean
  leadTemperatures?: LeadTemp[]
  minUsefulness?: number
  dateFrom?: string
  dateTo?: string
}

export interface CallsPage {
  items: CommunicationCall[]
  nextPageToken: string
}

interface ListCallsResponse {
  calls?: Array<Partial<CommunicationCall>>
  pagination?: { nextPageToken?: string }
}

export const communicationsApi = {
  /** GET /api/v1/messenger-providers/chats */
  async listChats(q: ChatsQuery): Promise<ChatsPage> {
    const params = new URLSearchParams()
    if (q.pageSize) params.set('pagination.pageSize', String(q.pageSize))
    if (q.pageToken) params.set('pagination.pageToken', q.pageToken)
    if (q.managerId) params.set('managerId', q.managerId)
    q.clientIds?.forEach((id) => params.append('clientIds', id))
    q.channels?.forEach((c) => params.append('channels', c))
    if (q.unreadOnly) params.set('unreadOnly', 'true')
    if (q.awaitingReply) params.set('awaitingReply', 'true')
    q.categories?.forEach((c) => params.append('categories', c))

    const { data } = await apiClient.get<ListChatsResponse>('/api/v1/messenger-providers/chats', {
      params,
    })

    return {
      items: (data.data ?? []).map((c) => ({
        clientId: c.clientId ?? '',
        clientName: c.clientName ?? '',
        chatKey: c.chatKey ?? '',
        accountManagerId: c.accountManagerId ?? '',
        responsibleManagerId: c.responsibleManagerId ?? '',
        lastMessageTime: c.lastMessageTime ?? '',
        lastMessagePreview: c.lastMessagePreview ?? '',
        lastMessageFromClient: c.lastMessageFromClient ?? false,
        lastMessageCall:
          c.lastMessageCall === 'missed' || c.lastMessageCall === 'call' ? c.lastMessageCall : '',
        lastMessageAuthorType: c.lastMessageAuthorType ?? '',
        unreadCount: Number(c.unreadCount ?? 0),
        channel: (c.channel ?? 'telegram') as ChatChannel,
        title: c.title ?? '',
        category: isChatCategory(c.category) ? c.category : 'client',
      })),
      nextPageToken: data.pagination?.nextPageToken ?? '',
    }
  },
  /**
   * GET /api/v1/messenger-providers/chats/unread-counts (DEV-2823). Бейджи вкладок: по категориям
   * число чатов с непрочитанными входящими при тех же фильтрах менеджера, клиентов и каналов.
   */
  async countUnreadChats(
    q: Pick<ChatsQuery, 'managerId' | 'clientIds' | 'channels'>,
  ): Promise<Record<ChatCategory, number>> {
    const params = new URLSearchParams()
    if (q.managerId) params.set('managerId', q.managerId)
    q.clientIds?.forEach((id) => params.append('clientIds', id))
    q.channels?.forEach((c) => params.append('channels', c))

    const { data } = await apiClient.get<Partial<Record<ChatCategory, number>>>(
      '/api/v1/messenger-providers/chats/unread-counts',
      { params },
    )

    return {
      client: Number(data.client ?? 0),
      agent: Number(data.agent ?? 0),
      other: Number(data.other ?? 0),
    }
  },
  /**
   * POST /api/v1/messenger-providers/chats/category (DEV-2807). Поправить разбор бота. Чат без
   * карточки, отмеченный клиентом, получает карточку лида; ответ — клиент чата, '' — без карточки.
   */
  async setChatCategory(
    channel: Exclude<ChatChannel, 'zalo'>,
    chatKey: string,
    category: ChatCategory,
  ): Promise<string> {
    const { data } = await apiClient.post<{ clientId?: string }>(
      '/api/v1/messenger-providers/chats/category',
      { channel, chatKey, category },
    )

    return data.clientId ?? ''
  },
  /** GET /api/v1/telephony/communications/calls */
  async listCalls(q: CallsQuery): Promise<CallsPage> {
    const params = new URLSearchParams()
    if (q.pageSize) params.set('pagination.pageSize', String(q.pageSize))
    if (q.pageToken) params.set('pagination.pageToken', q.pageToken)
    if (q.managerId) params.set('managerId', q.managerId)
    q.clientIds?.forEach((id) => params.append('clientIds', id))
    q.sources?.forEach((s) => params.append('sources', s))
    if (q.direction) params.set('direction', q.direction)
    if (q.answered !== undefined) params.set('answered', String(q.answered))
    q.leadTemperatures?.forEach((l) => params.append('leadTemperatures', l))
    if (q.minUsefulness) params.set('minUsefulness', String(q.minUsefulness))
    if (q.dateFrom) params.set('dateFrom', q.dateFrom)
    if (q.dateTo) params.set('dateTo', q.dateTo)

    const { data } = await apiClient.get<ListCallsResponse>(
      '/api/v1/telephony/communications/calls',
      { params },
    )

    return {
      items: (data.calls ?? []).map((c) => ({
        id: c.id ?? '',
        source: (c.source ?? 'telephony') as CallSource,
        direction: c.direction === 'out' ? 'out' : 'in',
        outcome: (c.outcome ?? 'answered') as CallOutcome,
        managerId: c.managerId ?? '',
        clientId: c.clientId ?? '',
        clientName: c.clientName ?? '',
        peerNumber: c.peerNumber ?? '',
        startedAt: c.startedAt ?? '',
        durationSec: Number(c.durationSec ?? 0),
        hasRecording: c.hasRecording ?? false,
        hasAnalysis: c.hasAnalysis ?? false,
        leadTemperature: (c.leadTemperature ?? '') as LeadTemp | '',
        usefulnessScore: Number(c.usefulnessScore ?? 0),
      })),
      nextPageToken: data.pagination?.nextPageToken ?? '',
    }
  },
  /**
   * GET /api/v1/telephony/communications/calls/awaiting-callback (DEV-2826). Бейдж «Звонки» в
   * меню: сколько клиентов и номеров за 7 дней не дозвонились и им ещё не перезвонили.
   */
  async countAwaitingCallback(): Promise<number> {
    const { data } = await apiClient.get<{ count?: number }>(
      '/api/v1/telephony/communications/calls/awaiting-callback',
    )

    return Number(data.count ?? 0)
  },
}
